import type { FC } from 'react';

interface Options {
  /** Compact is lossy (1234 becomes "1.2k"); pass false where the exact value is the point. */
  compact?: boolean;
}

export function formatNumber(num?: number | null, { compact = true }: Options = {}): string {
  if (num == null) return '-';
  // A dash reads as "no usage" in the billing tables, but an exact count of 0 is a real answer.
  if (num === 0) return compact ? '-' : '0';

  return (
    num
      .toLocaleString('en-US', {
        useGrouping: true,
        ...(compact && { notation: 'compact' }),
      })
      // CLDR's en compact pattern is "0K", but SI kilo is lowercase and K is kelvin. Intl
      // exposes no option for the case, so the suffix is corrected after the fact. See #3814.
      .replace('K', 'k')
  );
}

interface CompactNumberProps {
  value?: number | null;
  /** Counts show a real 0; billing figures show the "no usage" dash instead. */
  showZero?: boolean;
}

// A plain title, not an antd Tooltip: this sits in table cells that render once per row, and
// it has to survive being passed as a Badge count. Same approach as EventChildsCounter.
export const CompactNumber: FC<CompactNumberProps> = ({ value, showZero = false }) => (
  <span title={value != null ? formatNumber(value, { compact: false }) : undefined}>
    {showZero && value === 0 ? '0' : formatNumber(value)}
  </span>
);
